// src/admin/components/kits/KitModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import AdminModal from '../common/AdminModal';
import FormInput from '../../../shared/components/FormInput';

const EMPTY_FORM = {
  name: '',
  sku: '',
  revision: '',
  description: '',
};

function buildFormFromKit(kit) {
  if (!kit) return EMPTY_FORM;
  return {
    name: kit.name || '',
    sku: kit.sku || '',
    revision: kit.revision || '',
    description: kit.description || '',
  };
}

export default function KitModal({
  show,
  onClose,
  kit,
  onSave,
  saving = false,
  error = null,
}) {
  const isEditMode = !!kit;
  const [form, setForm] = useState(EMPTY_FORM);

  const openCountRef = useRef(0);
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (show) {
      openCountRef.current += 1;
      setFormKey(openCountRef.current);
      setForm(buildFormFromKit(kit));
    }
  }, [show, kit]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(form);
  };

  const isValid = form.name?.trim() && form.sku?.trim();

  const footer = (
    <div className="d-flex justify-content-between w-100">
      <button
        type="button"
        className="btn btn-link text-muted"
        onClick={onClose}
        disabled={saving}
      >
        <i className="bi bi-arrow-left me-1" /> Cancel
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleSave}
        disabled={!isValid || saving}
      >
        {saving ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" />
            Saving...
          </>
        ) : (
          (isEditMode ? 'Save changes' : 'Create kit')
        )}
      </button>
    </div>
  );

  return (
    <AdminModal
      show={show}
      onClose={onClose}
      title={isEditMode ? 'Edit kit' : 'New kit'}
      footer={footer}
      size=""
    >
      <div key={formKey}>
        <FormInput
          id="kit-name"
          label="Name"
          placeholder="e.g., Pump seal replacement kit"
          value={form.name}
          onChange={(e) => handleChange('name', e.target.value)}
          required
        />

        <FormInput
          id="kit-sku"
          label="SKU"
          placeholder="e.g., KIT-1234"
          value={form.sku}
          onChange={(e) => handleChange('sku', e.target.value)}
          helperText="This should match your ERP / parts catalog identifier."
          required
        />

        <FormInput
          id="kit-revision"
          label={<>Revision <span className="text-muted ms-1">(optional)</span></>}
          placeholder="e.g., Rev B"
          value={form.revision}
          onChange={(e) => handleChange('revision', e.target.value)}
        />

        <FormInput
          id="kit-description"
          label={<>Description <span className="text-muted ms-1">(optional)</span></>}
          placeholder="Short description of what this kit includes or when it should be used."
          value={form.description}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={3}
        />

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

KitModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  kit: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  error: PropTypes.string,
};

KitModal.defaultProps = {
  kit: null,
  saving: false,
  error: null,
};

